import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements, PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import { useAuth } from '../../contexts/AuthContext';
import { COLORS, TYPOGRAPHY } from '../../lib/styleGuide';
import { Lock } from 'react-iconly';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { createShopProfile } from '../../utils/sellerApi';

const ACCENT_GRAY = '#757575';
const ERROR_RED = '#F44336';


const SETUP_FEE = 10.00;

// Load Stripe outside of component to avoid recreating on every render
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Professional Payment Form Component (matches checkout page)
function OnboardingPaymentForm({ onNext, onBack, isFirst, clientSecret, setupFee, data }) {
  const stripe = useStripe();
  const elements = useElements();
  const { currentUser } = useAuth();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [cardComplete, setCardComplete] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(false);

  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    if (event.error) {
      setError(event.error.message);
    } else {
      setError('');
    }
  };

  // Auto-populate fields with user profile data
  useEffect(() => {
    if (currentUser) {
      if (currentUser.displayName) {
        setFullName(currentUser.displayName);
      }
      if (currentUser.email) {
        setEmail(currentUser.email);
      }
    }
  }, [currentUser]);

  // Setup Payment Request Button (Google Pay / Apple Pay)
  useEffect(() => {
    if (!stripe || !clientSecret) return;

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: 'Shop Setup Fee',
        amount: Math.round(setupFee * 100),
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    // Check if payment request is available and specifically supports Google Pay/Apple Pay
    pr.canMakePayment().then((result) => {
      console.log('🔍 Payment Request Debug:', {
        canMakePayment: !!result,
        result: result,
        stripe: !!stripe,
        clientSecret: !!clientSecret,
        setupFee: setupFee,
        userAgent: navigator.userAgent,
        isSecureContext: window.isSecureContext,
        hasPaymentRequest: 'PaymentRequest' in window,
        hasPaymentRequestEvent: 'PaymentRequestEvent' in window
      });
      
      if (result) {
        console.log('🔍 Payment Request Result Details:', {
          result: result,
          googlePay: result.googlePay,
          applePay: result.applePay,
          apps: result.apps,
          methods: result.methods,
          keys: Object.keys(result)
        });
        
        // More permissive detection - if Payment Request API returns a result,
        // it likely means Google Pay/Apple Pay are available
        // The specific detection was too restrictive
        setCanMakePayment(true);
        setPaymentRequest(pr);
        console.log('✅ Payment Request available - showing mobile payment section');
      } else {
        console.log('❌ Payment Request not available - falling back to card only');
        console.log('💡 This is normal for some browsers/devices. Payment Request API support varies.');
        setCanMakePayment(false);
        setPaymentRequest(null);
      }
    }).catch((error) => {
      console.log('❌ Payment Request error:', error);
      setCanMakePayment(false);
      setPaymentRequest(null);
    });

    pr.on('paymentmethod', async (ev) => {
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      console.log('🔍 Payment Request Button clicked!', {
        paymentMethod: ev.paymentMethod,
        payerName: ev.payerName,
        payerEmail: ev.payerEmail,
        userAgent: navigator.userAgent,
        isMobile: isMobile,
        clientSecret: clientSecret
      });
      
      setIsProcessing(true);
      setError('');

      try {
        // Confirm the PaymentIntent without handling potential next actions (yet).
        console.log('🔍 Mobile Payment: Confirming payment with clientSecret:', clientSecret);
        
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        );

        console.log('🔍 Mobile Payment: Confirmation result:', {
          confirmError: confirmError,
          paymentIntent: paymentIntent,
          status: paymentIntent?.status
        });

        if (confirmError) {
          // Report to the browser that the payment failed
          console.error('❌ Mobile Payment: Confirmation failed:', confirmError);
          ev.complete('fail');
          setError(confirmError.message);
          setIsProcessing(false);
          return;
        }

        // Report to the browser that the confirmation was successful
        ev.complete('success');

        // Check if the PaymentIntent requires any actions
        if (paymentIntent.status === 'requires_action') {
          console.log('🔍 Mobile Payment: Requires action, handling 3D Secure');
          // Let Stripe.js handle the rest of the payment flow
          const { error } = await stripe.confirmCardPayment(clientSecret);
          if (error) {
            console.error('❌ Mobile Payment: Action error:', error);
            setError(error.message);
            setIsProcessing(false);
            return;
          }
          console.log('✅ Mobile Payment: Action completed successfully');
        }

        // Payment succeeded
        if (paymentIntent.status === 'succeeded') {
          console.log('✅ Mobile Payment: Payment succeeded, calling handlePaymentSuccess');
          await handlePaymentSuccess();
        } else {
          console.error('❌ Mobile Payment: Payment not successful, status:', paymentIntent.status);
          setError(`Payment not successful. Status: ${paymentIntent.status}`);
          setIsProcessing(false);
        }
      } catch (err) {
        ev.complete('fail');
        setError(err.message);
        setIsProcessing(false);
      }
    });
  }, [stripe, clientSecret, setupFee]);

  // Shared payment success handler
  const handlePaymentSuccess = async () => {
    try {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      console.log('PaymentStep: Creating shop with data:', data);
      
      // Create shop profile with collected onboarding data
      const shopData = {
        sellerId: currentUser.uid,
        shopName: data.shopName,
        description: data.shopDescription,
        address: data.shopAddress?.address || '',
        street: data.shopAddress?.street || '',
        city: data.shopAddress?.city || '',
        state: data.shopAddress?.state || '',
        zip: data.shopAddress?.zip || '',
        lat: data.shopAddress?.lat || null,
        lng: data.shopAddress?.lng || null,
        coverPhotoURL: data.shopCoverPhotoUrl || '',
        foodType: data.foodType,
        foodOfferings: data.foodOfferings || [],
        email: currentUser.email,
        displayName: currentUser.displayName || data.shopName,
        setupFeePaid: true,
        setupFeePaidAt: new Date().toISOString(),
        onboardingCompleted: false,
        onboardingCompletedAt: null
      };

      console.log('PaymentStep: Creating shop with shopData:', shopData);
      const createdShop = await createShopProfile(shopData);
      console.log('PaymentStep: Shop created successfully:', createdShop);

      // Update user profile
      await updateDoc(doc(db, 'users', currentUser.uid), {
        setupFeePaid: true,
        setupFeePaidAt: new Date().toISOString(),
        onboardingCompleted: false, // Keep false until verification is complete
        onboardingCompletedAt: null,
        shopId: createdShop.id,
        role: 'creator', // Set user role to creator
        updatedAt: new Date().toISOString()
      });

      console.log('PaymentStep: User profile updated successfully');
      
      // Continue with onboarding flow (go to verification step)
      onNext();
      
    } catch (error) {
      console.error('PaymentStep: Error creating shop:', error);
      setError('Failed to create your shop. Please contact support.');
      // Don't redirect on error - let user try again
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!fullName.trim()) {
      setError('Please enter your full name.');
      return;
    }

    if (!email.trim()) {
      setError('Please enter your email.');
      return;
    }

    if (!stripe || !elements) {
      setError('Stripe is not loaded. Please refresh the page.');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      // Get card element
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      // Create payment method first
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: fullName,
          email: email,
        },
      });

      if (paymentMethodError) {
        throw new Error(paymentMethodError.message);
      }

      // Confirm the payment using the payment method
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: paymentMethod.id,
        },
        {
          return_url: window.location.origin + '/onboarding',
        }
      );

      if (confirmError) {
        throw new Error(confirmError.message);
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        await handlePaymentSuccess();
      } else {
        throw new Error('Payment was not successful');
      }
    } catch (err) {
      setError(err.message || 'Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Debug logging for render
  console.log('🔍 Payment Form Render Debug:', {
    canMakePayment,
    paymentRequest: !!paymentRequest,
    stripe: !!stripe,
    clientSecret: !!clientSecret,
    shouldShowMobilePayment: canMakePayment && paymentRequest,
    paymentRequestType: paymentRequest ? typeof paymentRequest : 'null'
  });

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div>
        <h2 style={{ fontWeight: 700, fontSize: TYPOGRAPHY.ONBOARDING.H2, margin: 0, color: '#222' }}>Setup Fee</h2>
        <p style={{ color: ACCENT_GRAY, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, margin: '10px 0 0 0' }}>
          A one-time setup fee helps us maintain the platform and support creators like you.
        </p>
      </div>

      {/* Fee Summary */}
      <div style={{ 
        background: '#F8F9FA', 
        borderRadius: 12, 
        padding: 20, 
        border: `2px solid #E9ECEF`,
        display: 'flex',
        flexDirection: 'column',
        gap: 12
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 600, fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, color: '#222' }}>Shop Setup Fee</span>
          <span style={{ fontWeight: 700, fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, color: COLORS.PRIMARY_GRADIENT_START }}>${SETUP_FEE.toFixed(2)}</span>
        </div>
        <div style={{ fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, color: ACCENT_GRAY }}>
          One-time payment • No recurring fees
        </div>
      </div>

      {/* Personal Information */}
      <div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ 
              fontWeight: 600, 
              color: ACCENT_GRAY, 
              fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, 
              display: 'block', 
              marginBottom: '4px' 
            }}>
              Full Name
            </label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Enter your full name"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box'
              }}
              required
            />
          </div>
          <div>
            <label style={{ 
              fontWeight: 600, 
              color: ACCENT_GRAY, 
              fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, 
              display: 'block', 
              marginBottom: '4px' 
            }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box'
              }}
              required
            />
          </div>
        </div>
      </div>

      {/* Mobile Payment Options (Google Pay / Apple Pay) - Only show if actually available */}
      {canMakePayment && paymentRequest ? (
        <div>
          <label style={{ fontWeight: 600, color: ACCENT_GRAY, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, display: 'block', marginBottom: 8 }}>
            Mobile Payment
          </label>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 60,
            width: '100%'
          }}>
            <div style={{ width: '100%', maxWidth: '300px' }}>
              <PaymentRequestButtonElement
                options={{
                  paymentRequest,
                  style: {
                    paymentRequestButton: {
                      theme: 'light',
                      height: '48px',
                      type: 'default',
                    },
                  },
                }}
                onReady={() => {
                  console.log('🔍 Payment Request Button ready');
                }}
                onClick={(event) => {
                  console.log('🔍 Payment Request Button clicked', event);
                }}
                onError={(error) => {
                  console.log('🔍 Payment Request Button error', error);
                }}
              />
            </div>
          </div>
          <div style={{ 
            fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, 
            color: ACCENT_GRAY, 
            textAlign: 'center', 
            marginTop: 8 
          }}>
            Or continue with card payment below
          </div>
        </div>
      ) : null}

      {/* Stripe CardElement - Card payment input */}
      <div>
        <label style={{ fontWeight: 600, color: ACCENT_GRAY, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, display: 'block', marginBottom: 8 }}>
          Card Details
        </label>
        <div style={{
          padding: 16,
          border: '2px solid #eee',
          borderRadius: 12,
          background: '#FAF9F7',
        }}>
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#222222',
                  fontFamily: 'Montserrat, sans-serif',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#df1b41',
                  iconColor: '#df1b41',
                },
              },
            }}
            onChange={handleCardChange}
          />
        </div>
      </div>

      {/* Security Message */}
      <div style={{ 
        background: '#F8F9FA', 
        borderRadius: 10, 
        padding: 16, 
        border: '1px solid #E9ECEF',
        display: 'flex',
        alignItems: 'center',
        gap: 12
      }}>
        <Lock
          set="regular"
          size={24}
          primaryColor={ACCENT_GRAY}
        />
        <div>
          <div style={{ fontWeight: 600, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, color: '#222', marginBottom: 2 }}>
            Secure Payment
          </div>
          <div style={{ fontSize: 13, color: ACCENT_GRAY }}>
            Your payment information is encrypted and secure. We never store your card details.
          </div>
        </div>
      </div>

      {/* Error Message */}
      <div style={{ minHeight: 22, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION }}>
        {error && <span style={{ color: ERROR_RED }}>{error}</span>}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        {!isFirst && (
          <button
            type="button"
            onClick={onBack}
            disabled={isProcessing}
            style={{
              background: '#fff',
              color: '#9CA3AF',
              border: 'none',
              borderRadius: 10,
              padding: '14px 32px',
              fontSize: TYPOGRAPHY.ONBOARDING.BUTTON_TEXT,
              fontWeight: 600,
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              minHeight: 44,
              minWidth: 120,
              transition: 'all 0.2s',
              opacity: isProcessing ? 0.6 : 1,
            }}
            aria-label="Back"
          >
            Back
          </button>
        )}
        <button
          type="submit"
          disabled={isProcessing || !fullName.trim() || !email.trim()}
          style={{
            background: !isProcessing && fullName.trim() && email.trim() ? COLORS.PRIMARY_GRADIENT : '#E0CFC2',
            color: '#fff',
            border: 'none',
            borderRadius: 10,
                          padding: '14px 32px',
            fontSize: TYPOGRAPHY.ONBOARDING.BUTTON_TEXT,
            fontWeight: 600,
            cursor: !isProcessing && fullName.trim() && email.trim() ? 'pointer' : 'not-allowed',
            minHeight: 44,
            minWidth: 120,
            boxShadow: '0 1px 4px #0001',
            transition: 'background 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
          aria-label={isProcessing ? 'Processing payment...' : 'Pay setup fee'}
        >
          {isProcessing ? (
            <>
              <div style={{ 
                width: 16, 
                height: 16, 
                border: '2px solid transparent', 
                borderTop: '2px solid #fff', 
                borderRadius: '50%', 
                animation: 'spin 1s linear infinite' 
              }} />
              Processing...
            </>
          ) : (
            `Pay $${SETUP_FEE.toFixed(2)}`
          )}
        </button>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `
      }} />
    </form>
  );
}

// Main PaymentStep Component
export default function PaymentStep({ data, onNext, onBack, isFirst, isLast }) {
  const { currentUser } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  
  console.log('PaymentStep: Component rendered, currentUser:', currentUser);
  console.log('PaymentStep: clientSecret state:', clientSecret);
  console.log('PaymentStep: error state:', error);

  useEffect(() => {
    // Create payment intent when component mounts
    const createPaymentIntent = async () => {
      // Set a timeout to prevent infinite loading
      const timeoutId = setTimeout(() => {
        if (!clientSecret) {
          console.error('PaymentStep: Payment intent creation timed out');
          setError('Payment initialization timed out. Please refresh the page.');
          setClientSecret('timeout');
        }
      }, 10000); // 10 second timeout
      try {
        console.log('PaymentStep: Starting payment intent creation...');
        console.log('PaymentStep: currentUser:', currentUser);
        console.log('PaymentStep: User agent:', navigator.userAgent);
        console.log('PaymentStep: Is mobile:', /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
        
        // Get authentication token
        if (!currentUser) {
          throw new Error('User not authenticated');
        }
        
        console.log('PaymentStep: Getting ID token...');
        const token = await currentUser.getIdToken();
        
        const requestBody = {
          amount: SETUP_FEE,
          currency: 'usd',
          items: [{ name: 'Shop Setup Fee', quantity: 1, price: SETUP_FEE }],
          fulfillment: 'setup_fee',
          address: '',
          note: 'Shop setup fee payment'
        };
        
        console.log('PaymentStep: Making API call to create payment intent...');
        console.log('PaymentStep: Request body:', requestBody);
        console.log('PaymentStep: SETUP_FEE value:', SETUP_FEE);
        console.log('PaymentStep: Amount being sent:', requestBody.amount);
        console.log('PaymentStep: Items price:', requestBody.items[0].price);
        
        // Detect mobile for CORS proxy fallback
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        
        // Try multiple approaches for mobile connectivity
        let response;
        let lastError;
        
        // First try direct API call
        try {
          console.log('PaymentStep: Attempting direct API call');
          
          response = await fetch('https://us-central1-marketplace-5fd46.cloudfunctions.net/createPaymentIntent', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(requestBody),
            mode: 'cors',
            credentials: 'omit'
          });
          
          console.log('PaymentStep: Direct API call successful, status:', response.status);
          
        } catch (directError) {
          console.error('PaymentStep: Direct API call failed:', directError);
          lastError = directError;
          
          // Try CORS proxy for mobile
          if (isMobile) {
            try {
              console.log('PaymentStep: Attempting CORS proxy for mobile');
              
              response = await fetch('https://corsproxy.io/?https://us-central1-marketplace-5fd46.cloudfunctions.net/createPaymentIntent', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(requestBody),
                mode: 'cors',
                credentials: 'omit'
              });
              
              console.log('PaymentStep: CORS proxy call successful, status:', response.status);
              
            } catch (proxyError) {
              console.error('PaymentStep: CORS proxy call failed:', proxyError);
              lastError = proxyError;
            }
          }
        }
        
        if (!response) {
          throw lastError || new Error('All API attempts failed');
        }

        console.log('PaymentStep: API response status:', response.status);
        console.log('PaymentStep: API response ok:', response.ok);

        if (!response.ok) {
          const errorText = await response.text();
          console.log('PaymentStep: API error response:', errorText);
          throw new Error('Failed to create payment intent');
        }

        const responseData = await response.json();
        console.log('PaymentStep: API response data:', responseData);
        console.log('PaymentStep: Payment intent amount:', responseData.paymentIntent?.amount);
        console.log('PaymentStep: Payment intent amount in dollars:', responseData.paymentIntent?.amount / 100);
        
        // Extract client_secret from the nested paymentIntent object
        const secret = responseData.paymentIntent?.client_secret;
        console.log('PaymentStep: Setting clientSecret:', secret ? 'present' : 'missing');
        setClientSecret(secret);
        clearTimeout(timeoutId); // Clear timeout on success
      } catch (err) {
        console.error('PaymentStep: Error creating payment intent:', err);
        setError(`Failed to initialize payment: ${err.message}`);
        // Set a fallback client secret to prevent infinite loading
        setClientSecret('fallback');
        clearTimeout(timeoutId); // Clear timeout on error
      }
    };

    createPaymentIntent();
  }, [currentUser]);

  // Check if user is authenticated
  if (!currentUser) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        padding: '40px',
        gap: '16px'
      }}>
        <div style={{ color: ERROR_RED, fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING }}>
          Please log in to continue with payment setup.
        </div>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        padding: '40px',
        gap: '16px'
      }}>
        <div style={{ 
          width: 32, 
          height: 32, 
          border: '3px solid transparent', 
          borderTop: '3px solid #C97B53', 
          borderRadius: '50%', 
          animation: 'spin 1s linear infinite' 
        }} />
        <div style={{ color: ACCENT_GRAY, fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING }}>
          Initializing payment form...
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      
      <OnboardingPaymentForm 
        onNext={onNext}
        onBack={onBack}
        isFirst={isFirst}
        clientSecret={clientSecret}
        setupFee={SETUP_FEE}
        data={data}
      />
    </Elements>
  );
} 